import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import { fmtFCFA } from "@/lib/format";
import { useOrdersList } from "@/lib/orders/orders-hooks";
import type { OrderStatus, OrdersFilter } from "@/lib/orders/types";
import { statusOrderV2 } from "@/lib/status";

const ALL_STATUSES: OrderStatus[] = [
	"DRAFT",
	"PENDING_PAYMENT",
	"PAID",
	"CONFIRMED",
	"PROCURING",
	"IN_HUB",
	"READY_TO_SHIP",
	"SHIPPING",
	"DELIVERED",
	"COMPLETED",
	"CANCELLED",
	"REFUNDED",
	"DISPUTED",
];

export function AdminOrdersSearchPage() {
	const navigate = useNavigate();
	const [statuses, setStatuses] = useState<OrderStatus[]>([]);
	const [number, setNumber] = useState("");
	const [customerQuery, setCustomerQuery] = useState(
		() => new URLSearchParams(window.location.search).get("q") ?? "",
	);
	const [amountMin, setAmountMin] = useState<number | "">("");
	const [amountMax, setAmountMax] = useState<number | "">("");
	const [dateFrom, setDateFrom] = useState("");
	const [dateTo, setDateTo] = useState("");

	const filter: OrdersFilter = useMemo(
		() => ({
			status: statuses.length ? statuses : undefined,
			number: number || undefined,
			customerQuery: customerQuery || undefined,
			amountMin: amountMin === "" ? undefined : Number(amountMin),
			amountMax: amountMax === "" ? undefined : Number(amountMax),
			dateFrom: dateFrom || undefined,
			dateTo: dateTo || undefined,
			take: 100,
		}),
		[statuses, number, customerQuery, amountMin, amountMax, dateFrom, dateTo],
	);

	const { data, isLoading } = useOrdersList(filter);

	const clearAll = () => {
		setStatuses([]);
		setNumber("");
		setCustomerQuery("");
		setAmountMin("");
		setAmountMax("");
		setDateFrom("");
		setDateTo("");
	};

	const activeFilters =
		statuses.length +
		(number ? 1 : 0) +
		(customerQuery ? 1 : 0) +
		(amountMin !== "" ? 1 : 0) +
		(amountMax !== "" ? 1 : 0) +
		(dateFrom ? 1 : 0) +
		(dateTo ? 1 : 0);

	return (
		<>
			<PageHead
				title="Recherche avancée"
				sub={`ORD-06 — ${activeFilters} filtre(s) actif(s)`}
				actions={
					activeFilters > 0 ? (
						<AdminButton
							variant="ghost"
							onClick={clearAll}
							data-testid="clear-filters"
						>
							<AdminIcon n="refresh" s={14} />
							Effacer les filtres
						</AdminButton>
					) : null
				}
			/>

			<div className="stack">
				<Card>
					<CardHead title="Filtres" />
					<CardBody>
						<div className="search-filters-grid">
							<div className="search-field">
								<label>N° commande</label>
								<input
									type="text"
									value={number}
									onChange={(e) => setNumber(e.target.value)}
									placeholder="AKW-2026-…"
									data-testid="filter-number"
								/>
							</div>
							<div className="search-field">
								<label>Client (email/nom/tel)</label>
								<input
									type="text"
									value={customerQuery}
									onChange={(e) => setCustomerQuery(e.target.value)}
									placeholder="ex: bob@…"
									data-testid="filter-customer"
								/>
							</div>
							<div className="search-field">
								<label>Montant min (FCFA)</label>
								<input
									type="number"
									value={amountMin}
									onChange={(e) =>
										setAmountMin(
											e.target.value === "" ? "" : Number(e.target.value),
										)
									}
									placeholder="0"
									data-testid="filter-amount-min"
								/>
							</div>
							<div className="search-field">
								<label>Montant max (FCFA)</label>
								<input
									type="number"
									value={amountMax}
									onChange={(e) =>
										setAmountMax(
											e.target.value === "" ? "" : Number(e.target.value),
										)
									}
									placeholder="1 000 000"
									data-testid="filter-amount-max"
								/>
							</div>
							<div className="search-field">
								<label>Date depuis</label>
								<input
									type="date"
									value={dateFrom}
									onChange={(e) => setDateFrom(e.target.value)}
									data-testid="filter-date-from"
								/>
							</div>
							<div className="search-field">
								<label>Date jusqu'à</label>
								<input
									type="date"
									value={dateTo}
									onChange={(e) => setDateTo(e.target.value)}
									data-testid="filter-date-to"
								/>
							</div>
						</div>

						<div style={{ marginTop: 16 }}>
							<div className="search-section-label">
								Statuts (multi-sélection)
							</div>
							<div className="status-chip-row">
								{ALL_STATUSES.map((s) => {
									const isOn = statuses.includes(s);
									return (
										<button
											type="button"
											key={s}
											onClick={() =>
												setStatuses((p) =>
													p.includes(s) ? p.filter((x) => x !== s) : [...p, s],
												)
											}
											data-testid={`filter-status-${s}`}
											className={`status-chip ${isOn ? "status-chip--on" : ""}`}
										>
											{statusOrderV2(s).l}
										</button>
									);
								})}
							</div>
						</div>
					</CardBody>
				</Card>

				<Card>
					<CardHead title={`Résultats (${data?.total ?? 0})`} />
					<div className="table-wrap" style={{ margin: 0, overflowX: "auto" }}>
						{isLoading ? (
							<div style={{ padding: 24 }} className="muted">
								Chargement…
							</div>
						) : (data?.items ?? []).length === 0 ? (
							<div
								style={{ padding: 40, textAlign: "center" }}
								className="muted"
								data-testid="search-empty"
							>
								Aucune commande ne correspond à ces critères.
							</div>
						) : (
							<table className="table">
								<thead>
									<tr>
										<th>N°</th>
										<th>Statut</th>
										<th>Client</th>
										<th>Total</th>
										<th>Date</th>
									</tr>
								</thead>
								<tbody>
									{(data?.items ?? []).map((o) => (
										<tr
											key={o.id}
											onClick={() => navigate(`/commandes/${o.id}`)}
											style={{ cursor: "pointer" }}
											data-testid={`result-row-${o.number}`}
										>
											<td className="mono">{o.number}</td>
											<td>
												<Tag variant={statusOrderV2(o.status).cls}>
													{statusOrderV2(o.status).l}
												</Tag>
											</td>
											<td>
												{o.customer?.firstName} {o.customer?.lastName}
											</td>
											<td className="right tn">{fmtFCFA(o.totalAmount)}</td>
											<td>{new Date(o.createdAt).toLocaleString("fr-FR")}</td>
										</tr>
									))}
								</tbody>
							</table>
						)}
					</div>
				</Card>
			</div>

			<style>{`
        .search-filters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 14px;
        }
        .search-field label {
          margin-bottom: 4px;
          font-size: 12px;
          color: var(--a-ink-soft);
          font-weight: 500;
        }
        .search-section-label {
          font-size: 12px;
          color: var(--a-ink-soft);
          font-weight: 500;
          margin-bottom: 8px;
        }
        .status-chip-row {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .status-chip {
          padding: 5px 11px;
          border-radius: 14px;
          border: 1px solid var(--a-border-strong);
          background: var(--a-surface);
          color: var(--a-ink);
          font-size: 12px;
          cursor: pointer;
          transition: background 120ms, color 120ms, border-color 120ms;
        }
        .status-chip:hover { border-color: var(--a-terracotta); }
        .status-chip--on {
          background: var(--a-terracotta);
          color: #fff;
          border-color: var(--a-terracotta);
        }
      `}</style>
		</>
	);
}
